import type { StoreCollection, StoreProductCategory } from "@medusajs/types";
import {
  queryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { medusa } from "#/lib/medusa";
import type { Brand, Category } from "#/lib/medusa-types/catalog";

export type { Brand, Category } from "#/lib/medusa-types/catalog";

const TAXONOMY_STALE_MS = 5 * 60 * 1000;

export const taxonomyKeys = {
  all: ["storefront", "taxonomy"] as const,
  brands: ["storefront", "taxonomy", "brands"] as const,
  categories: ["storefront", "taxonomy", "categories"] as const,
};

function readMetaString(
  meta: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const v = meta?.[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}

function readMetaBool(
  meta: Record<string, unknown> | null | undefined,
  key: string,
): boolean {
  const v = meta?.[key];
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v === "true";
  return false;
}

function readMetaInt(
  meta: Record<string, unknown> | null | undefined,
  key: string,
): number | null {
  const v = meta?.[key];
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === "string" && v.length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  }
  return null;
}

function projectCollection(c: StoreCollection): Brand {
  const meta = c.metadata ?? null;
  return {
    id: c.id,
    slug: c.handle,
    name: c.title,
    featured: readMetaBool(meta, "featured"),
    position: readMetaInt(meta, "position") ?? 0,
    logoUrl:
      readMetaString(meta, "logoUrl") ?? readMetaString(meta, "logo_url"),
  };
}

function projectCategory(c: StoreProductCategory): Category {
  const meta = c.metadata ?? null;
  return {
    id: c.id,
    slug: c.handle,
    name: c.name,
    imageUrl:
      readMetaString(meta, "imageUrl") ??
      readMetaString(meta, "image_url") ??
      "",
    position: c.rank ?? 0,
  };
}

async function fetchBrands(): Promise<Brand[]> {
  const { collections } = await medusa.store.collection.list({ limit: 100 });
  const items = collections.map(projectCollection);
  items.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  return items;
}

async function fetchCategories(): Promise<Category[]> {
  const { product_categories } = await medusa.store.category.list({
    limit: 100,
  });
  const items = product_categories.map(projectCategory);
  items.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  return items;
}

export const taxonomyQueries = {
  brands: () =>
    queryOptions({
      queryKey: taxonomyKeys.brands,
      queryFn: fetchBrands,
      staleTime: TAXONOMY_STALE_MS,
    }),
  categories: () =>
    queryOptions({
      queryKey: taxonomyKeys.categories,
      queryFn: fetchCategories,
      staleTime: TAXONOMY_STALE_MS,
    }),
};

export function useBrands(): UseQueryResult<Brand[]> {
  return useQuery(taxonomyQueries.brands());
}

export function useCategories(): UseQueryResult<Category[]> {
  return useQuery(taxonomyQueries.categories());
}
